import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Grid, Typography } from '@mui/material';
import ArrowBackIosRoundedIcon from '@mui/icons-material/ArrowBackIosRounded';
import {
  getBillingConsolidatedInvoiceDetails,
  getBillingInvoiceDetails,
  getConsolidateInvoiceMatrix,
  getBillSelfConsolidatedInvoiceDetails,
} from 'redux/actions/BillingAction';
import Loader from 'components/loader/Loader';
import { consolidateInvoiceData, billSelfConsolidateInvoiceData } from './BillingInvoiceData';
import BillingConsolidatedInvoiceTable from './BillingConsolidatedInvoiceTable';
import CommonButton from 'components/commonButton/CommonButton';
import ConfirmAndLinkModel from 'components/commonConfirmAndLinkModel/ConfirmAndLinkModel';

const BillingConsolidateInvoice = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();

  const pathName = location?.pathname?.split('/');
  const [searchParams] = useSearchParams();

  const params = Object.fromEntries([...searchParams]);

  const queryParams = {
    cityId: params?.cityId || '',
    cityName: params?.cityName || '',
    companyId: params?.companyId || '',
    companyName: params?.companyName || '',
    tripId: params?.tripId || '',
    page: params?.page || '',
    pageRowSize: params?.pageRowSize || '',
    status: params?.status || '',
    fromDate: params?.fromDate === 'undefined' ? '' : params?.fromDate ? params?.fromDate : '',
    toDate: params?.fromDate === 'undefined' ? '' : params?.toDate ? params?.toDate : '',
    magicToken: params?.magicToken || '',
    pageType: params?.pageType || '',
    billingType: params?.billingType || '',
    employeeId: params?.employeeId || '',
    employeeName: params?.employeeName || '',
    from: params?.from || '',
  };
  const consolidationInvoiceMatrix = useSelector(
    (state) => state?.getConsolidateInvoiceMatrix?.consolidateInvoiceMatrix?.data,
  );
  // const consolidateInvoiceDetails = consolidateInvoiceData(
  //   consolidationInvoiceMatrix,
  // );
  const consolidateIvoiceList = useSelector(
    (state) => state?.getBillingConsolidatedInvoiceDetails?.bilingConsolidatedInvoice?.data,
  );
  const consolidateIvoiceLoader = useSelector(
    (state) => state?.getBillingConsolidatedInvoiceDetails?.loading,
  );

  const billSelfInvoiceDetails = useSelector(
    (state) => state?.getBillSelfConsolidatedInvoiceDetails?.consolidatedInvoiceDetails,
  );
  const billSelfInvoiceLoader = useSelector(
    (state) => state?.getBillSelfConsolidatedInvoiceDetails?.loading,
  );

  const isBillToSelf = queryParams?.billingType === 'billToCreditCard';
  const consolidateInvoiceDetails = isBillToSelf
    ? billSelfConsolidateInvoiceData(billSelfInvoiceDetails, queryParams?.cityName)
    : consolidateInvoiceData(consolidationInvoiceMatrix, queryParams);

  const formatDateToDDMMYYYY = (dateValue) => {
    if (!dateValue) return '';

    return dateValue.slice(0, 10).split('-').reverse().join('/');
  };

  const billSelfInvoiceTableData = {
    ...billSelfInvoiceDetails,

    body:
      billSelfInvoiceDetails?.body?.map((row) => ({
        ...row,

        // Readable consolidated invoice number:
        consolidatedInvoiceNumber: billSelfInvoiceDetails?.header?.invoiceId || '',
        tripDate: formatDateToDDMMYYYY(row?.dutyDate),
        additionalAmount:
          row?.additionalAmount ?? row?.additionalAmt ?? row?.additionalCharges ?? 0,
      })) || [],

    headers: [
      { label: 'Sr. No.', key: 'srNo' },
      { label: 'Booking No.', key: 'bookNo' },
      { label: 'Duty Date', key: 'dutyDate' },
      { label: 'Duty Type', key: 'dutyType' },
      { label: 'KM Rate', key: 'kmRate' },
      { label: 'HR Rate', key: 'hrRate' },
      { label: 'Total KM', key: 'totalKm' },
      { label: 'Total HR', key: 'totalHr' },
      { label: 'Driver Allowance', key: 'driverAllowance' },
      { label: 'Toll & Parking', key: 'tollParkingAmt' },
      { label: 'Total Rent', key: 'totalRent' },
      { label: 'Additional Amount', key: 'additionalAmount' },
      { label: 'GST', key: 'gstAmount' },
      { label: 'Grand Total', key: 'grandTotal' },
    ],
  };

  const consolidateIvoiceListWithAdditional = (() => {
    if (!consolidateIvoiceList || isBillToSelf) return consolidateIvoiceList;
    const hasAdditional =
      consolidateIvoiceList?.headers?.some?.((h) => h.key === 'additionalAmount') ||
      consolidateIvoiceList?.header?.some?.((h) => h.key === 'additionalAmount');
    if (hasAdditional) return consolidateIvoiceList;

    const headers = consolidateIvoiceList?.headers || consolidateIvoiceList?.header || [];
    const gstIdx = headers.findIndex(
      (h) =>
        h.key === 'gstAmount' ||
        h.key === 'gstAmt' ||
        h.key === 'gst' ||
        h.label?.toLowerCase() === 'gst',
    );

    const additionalHeader = { label: 'Additional Amount', key: 'additionalAmount' };
    const updatedHeaders = [...headers];
    if (gstIdx !== -1) {
      updatedHeaders.splice(gstIdx, 0, additionalHeader);
    } else {
      updatedHeaders.push(additionalHeader);
    }

    // Find the key used for the GST column in API-returned headers
    const gstHeader = headers.find(
      (h) =>
        h.key === 'gstAmount' ||
        h.key === 'gstAmt' ||
        h.key === 'gst' ||
        h.label?.toLowerCase() === 'gst',
    );
    const gstKey = gstHeader?.key || 'gstAmount';

    return {
      ...consolidateIvoiceList,
      headers: updatedHeaders,
      body:
        consolidateIvoiceList?.body?.map((row) => {
          // Prefer direct gstAmount from API; fallback to cgst+sgst+igst sum
          const resolvedGst =
            parseFloat(row?.gstAmount) ||
            parseFloat(row?.cgstAmount || 0) +
              parseFloat(row?.sgstAmount || 0) +
              parseFloat(row?.igstAmount || 0) ||
            0;
          return {
            ...row,
            additionalAmount:
              row?.additionalAmount ?? row?.additionalAmt ?? row?.additionalCharges ?? 0,
            [gstKey]: resolvedGst,
          };
        }) || [],
    };
  })();

  const activeInvoiceList = isBillToSelf
    ? billSelfInvoiceTableData
    : consolidateIvoiceListWithAdditional;
  const activeInvoiceLoader = isBillToSelf ? billSelfInvoiceLoader : consolidateIvoiceLoader;

  // ── MODAL STATES ──
  const [paymentLinkOpen, setPaymentLinkOpen] = useState(false);
  const [sendInvoiceOpen, setSendInvoiceOpen] = useState(false);

  const paymentLinkRecipientName = isBillToSelf
    ? billSelfInvoiceDetails?.header?.RecipientEmployee?.name || 'NA'
    : queryParams?.companyName || 'NA';

  const getConsolidatedTotalAmount = () => {
    if (isBillToSelf) {
      return billSelfInvoiceDetails?.header?.totalAmount || 0;
    }

    if (consolidationInvoiceMatrix?.totalAmount) {
      return consolidationInvoiceMatrix.totalAmount;
    }
    if (consolidateIvoiceList?.totalAmount) {
      return consolidateIvoiceList.totalAmount;
    }
    if (consolidateIvoiceList?.body?.length) {
      return consolidateIvoiceList.body.reduce((sum, item) => {
        const val = item?.totalAmount || item?.amount || item?.price || item?.total || 0;
        return sum + Number(val);
      }, 0);
    }
    return 0;
  };

  const getConsolidatedPaymentStatus = () => {
    if (isBillToSelf) {
      return (
        billSelfInvoiceDetails?.header?.paymentStatus ||
        billSelfInvoiceDetails?.paymentStatus ||
        'Unpaid'
      );
    }
    if (consolidationInvoiceMatrix?.paymentStatus) {
      return consolidationInvoiceMatrix.paymentStatus;
    }
    if (consolidateIvoiceList?.paymentStatus) {
      return consolidateIvoiceList.paymentStatus;
    }
    const bodyPaymentStatus =
      consolidateIvoiceList?.body?.[0]?.Payments?.[0]?.status ||
      consolidateIvoiceList?.body?.[0]?.Payment?.[0]?.status ||
      consolidateIvoiceList?.body?.[0]?.paymentStatus;
    if (bodyPaymentStatus) {
      return bodyPaymentStatus;
    }
    return 'Unpaid';
  };

  useEffect(() => {
    if (isBillToSelf) {
      dispatch(getBillSelfConsolidatedInvoiceDetails(queryParams?.tripId));
    } else if (pathName?.includes('operation')) {
      dispatch(
        getBillingConsolidatedInvoiceDetails(queryParams?.magicToken || queryParams?.tripId),
      );
      dispatch(getConsolidateInvoiceMatrix(queryParams?.magicToken || queryParams?.tripId));
    } else {
      dispatch(getBillingConsolidatedInvoiceDetails(queryParams?.magicToken, 'public'));
      dispatch(getConsolidateInvoiceMatrix(queryParams?.magicToken, 'public'));
    }
  }, []);

  const commonGridRender = (arrayData, borderType, gridSize, stackLayout = false) => {
    return (
      <Grid
        item
        md={gridSize?.md}
        className={`${
          borderType === 'borderRight'
            ? 'solidBorderRight1p'
            : borderType === 'borderLeft'
              ? 'solidBorderLeft1p'
              : ''
        } p20`}
      >
        {arrayData &&
          arrayData?.map((data, idx) => (
            <Grid container key={idx} className="pb20">
              <Grid item xs={stackLayout ? 12 : 5} className={stackLayout ? '' : 'pr10'}>
                <Typography
                  variant="body4"
                  component={'p'}
                  className="colorGrey"
                  sx={{ wordBreak: 'break-word' }}
                >
                  {data?.title}
                </Typography>
              </Grid>
              <Grid item xs={stackLayout ? 12 : 7} className={stackLayout ? '' : 'pl10'}>
                <Typography
                  variant="body4"
                  component={'p'}
                  className="colorBlack"
                  sx={{ wordBreak: 'break-word' }}
                >
                  {data?.value}
                </Typography>
              </Grid>
            </Grid>
          ))}
      </Grid>
    );
  };

  return (
    <Grid container className="pt20 br20" justifyContent={'center'}>
      {activeInvoiceLoader ? (
        <Loader />
      ) : (
        <Grid item md={11}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              width: '100%',
              mb: 2,
            }}
          >
            {pathName?.includes('operation') ? (
              <ArrowBackIosRoundedIcon
                className="font24 colorGrey"
                role="presentation"
                onClick={() => {
                  if (queryParams?.from === 'manage-invoice') {
                    if (isBillToSelf) {
                      navigate(
                        `/manage-invoice?invoiceTab=consolidated&companyId=${queryParams?.companyId}&companyName=${queryParams?.companyName}&cityId=${queryParams?.cityId}&cityName=${queryParams?.cityName}&status=${queryParams?.status}&page=${queryParams?.page}&pageRowSize=${queryParams?.pageRowSize}&billingType=billToCreditCard&employeeId=${queryParams?.employeeId}&employeeName=${queryParams?.employeeName}`,
                      );
                    } else {
                      navigate(
                        `/manage-invoice?invoiceTab=consolidated&companyId=${queryParams?.companyId}&companyName=${queryParams?.companyName}&cityId=${queryParams?.cityId}&cityName=${queryParams?.cityName}&status=${queryParams?.status}&page=${queryParams?.page}&pageRowSize=${queryParams?.pageRowSize}&fromDate=${queryParams?.fromDate}&toDate=${queryParams?.toDate}`,
                      );
                    }
                  } else {
                    if (isBillToSelf) {
                      navigate(
                        `/operation/billing/billToCreditCard?companyId=${queryParams?.companyId}&companyName=${queryParams?.companyName}&cityId=${queryParams?.cityId}&cityName=${queryParams?.cityName}&status=${queryParams?.status}&page=${queryParams?.page}&pageRowSize=${queryParams?.pageRowSize}&billingType=billToCreditCard&employeeId=${queryParams?.employeeId}&employeeName=${queryParams?.employeeName}`,
                      );
                    } else {
                      navigate(
                        `/operation/billing/billToCompany?companyId=${queryParams?.companyId}&companyName=${queryParams?.companyName}&cityId=${queryParams?.cityId}&cityName=${queryParams?.cityName}&status=${queryParams?.status}&page=${queryParams?.page}&pageRowSize=${queryParams?.pageRowSize}&fromDate=${queryParams?.fromDate}&toDate=${queryParams?.toDate}`,
                      );
                    }
                  }
                }}
              />
            ) : (
              <Box />
            )}
            {pathName?.includes('operation') && (
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                {['success', 'paid'].includes(getConsolidatedPaymentStatus()?.toLowerCase()) ? (
                  <Typography
                    variant="body2"
                    sx={{
                      color: '#2E7D32',
                      fontWeight: 600,
                      fontSize: '14px',
                      mr: 1,
                    }}
                  >
                    Payment Status: Paid
                  </Typography>
                ) : (
                  <CommonButton
                    name="Payment Link"
                    btnClass="btnSm btnOutlined"
                    onClick={() => setPaymentLinkOpen(true)}
                  />
                )}
                <CommonButton
                  name="Send Invoice"
                  btnClass="btnSm"
                  onClick={() => setSendInvoiceOpen(true)}
                />
              </Box>
            )}
          </Box>
          <Grid container className="commonBorder br20 mb40 solidBorderBottom1p">
            <Grid container item xs={12} md={9}>
              <Grid container item xs={12} className="solidBorderBottom1p solidBorderRight1p">
                <Grid item md={4} className="solidBorderRight1p"></Grid>
                <Grid item md={4} className="solidBorderRight1p">
                  {/* TODO: Api is pending from backend, so currently we are using static data*/}
                  <Typography className="colorPrimary ptb20 textCenter" component={'p'}>
                    {isBillToSelf
                      ? 'Tax Invoice'
                      : `${consolidationInvoiceMatrix?.supplierCompanyName || ''} Tax Invoice`}
                  </Typography>
                </Grid>
                <Grid item md={4}>
                  <Typography className="colorPrimary ptb20 textCenter" component={'p'}>
                    Original For Recipient
                  </Typography>
                </Grid>
              </Grid>
              <Grid container item xs={12} className="solidBorderBottom1p solidBorderRight1p">
                <Grid item md={4} className="solidBorderRight1p">
                  <Typography className="colorBlack ptb20 pl20" component={'p'} variant="body4">
                    Billing Branch
                  </Typography>
                </Grid>
                <Grid item md={8} className="p20">
                  <Typography className="colorBlack" variant="body4">
                    {consolidationInvoiceMatrix?.supplierCompanyAddress || ''}
                  </Typography>
                </Grid>
              </Grid>
              {commonGridRender(
                consolidateInvoiceDetails?.companyAckDetails,
                'borderRight',
                isBillToSelf ? { md: 6 } : { md: 4 },
              )}

              {commonGridRender(
                consolidateInvoiceDetails?.companyCinDetails,
                'borderRight',
                isBillToSelf ? { md: 6 } : { md: 4 },
              )}

              {!isBillToSelf &&
                commonGridRender(consolidateInvoiceDetails?.companyNameDetails, 'borderRight', {
                  md: 4,
                })}
            </Grid>

            <Grid className="justifyCenter" item xs={12} md={3}>
              <Box className="wh180"></Box>
            </Grid>

            <Grid container className="solidBorderTop1p">
              {commonGridRender(consolidateInvoiceDetails?.companyDetails, 'borderRight', {
                md: 4.5,
              })}

              {commonGridRender(consolidateInvoiceDetails?.companyPanDetails, 'borderRight', {
                md: 4.5,
              })}

              {commonGridRender(
                consolidateInvoiceDetails?.companyInvoiceDetails,
                'borderLeft',
                { md: 3 },
                true,
              )}
            </Grid>
          </Grid>
          <BillingConsolidatedInvoiceTable data={activeInvoiceList} />
        </Grid>
      )}

      {paymentLinkOpen && (
        <ConfirmAndLinkModel
          open={paymentLinkOpen}
          handleClose={() => setPaymentLinkOpen(false)}
          selectType="paymentLink"
          consolidatedInvoiceId={queryParams?.magicToken || queryParams?.tripId}
          companyName={paymentLinkRecipientName}
          totalAmount={{
            id: [queryParams?.magicToken || queryParams?.tripId],
            amount: getConsolidatedTotalAmount(),
          }}
          paymentStatus={getConsolidatedPaymentStatus()}
          onModalClose={() => {
            if (isBillToSelf) {
              dispatch(getBillSelfConsolidatedInvoiceDetails(queryParams?.tripId));
            } else {
              dispatch(
                getBillingConsolidatedInvoiceDetails(
                  queryParams?.magicToken || queryParams?.tripId,
                ),
              );
              dispatch(getConsolidateInvoiceMatrix(queryParams?.magicToken || queryParams?.tripId));
            }
          }}
        />
      )}

      {sendInvoiceOpen && (
        <ConfirmAndLinkModel
          open={sendInvoiceOpen}
          handleClose={() => setSendInvoiceOpen(false)}
          selectType="sendConsolidatedInvoice"
          consolidatedInvoiceId={queryParams?.magicToken || queryParams?.tripId}
          companyName={paymentLinkRecipientName}
          defaultEmail={
            isBillToSelf ? billSelfInvoiceDetails?.header?.RecipientEmployee?.email || '' : ''
          }
          totalAmount={{
            id: [queryParams?.magicToken || queryParams?.tripId],
            amount:
              billSelfInvoiceDetails?.header?.outstandingAmt ??
              consolidationInvoiceMatrix?.outstandingAmt ??
              consolidationInvoiceMatrix?.outstandingAmount ??
              consolidateIvoiceList?.outstandingAmt ??
              consolidateIvoiceList?.outstandingAmount ??
              0,
          }}
          paymentStatus={getConsolidatedPaymentStatus()}
          onModalClose={() => {
            if (isBillToSelf) {
              dispatch(getBillSelfConsolidatedInvoiceDetails(queryParams?.tripId));
            } else {
              dispatch(
                getBillingConsolidatedInvoiceDetails(
                  queryParams?.magicToken || queryParams?.tripId,
                ),
              );
              dispatch(getConsolidateInvoiceMatrix(queryParams?.magicToken || queryParams?.tripId));
            }
          }}
        />
      )}
    </Grid>
  );
};

export default BillingConsolidateInvoice;
